/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { TManager } from "apis/visitors/type";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { member, util } from "recoil/recoilState";
import Icon from "uis/Icon";
import Button from "uis/Button";
import { translate } from "./translate";
import SearchManagerItem from "uis/layout/search-manager-item";
import InputField from "uis/InputField";
import { theme } from "styles/theme";
import { TMember } from "types/user.types";
import { searchMembers } from "apis/members";

const isValidEmail = (email: string) => {
  const regex = new RegExp("[a-z0-9]+@[a-z]+.[a-z]{2,3}");
  return regex.test(email);
};

export const VisitManagerSection = ({ defaultManagers, onChange }: { defaultManagers: TMember[]; onChange?: (value: TMember[]) => void }) => {
  const memberInfo = useRecoilValue(member);
  const [searchMember, setSearchMember] = useState<TMember[]>([]);
  const { language } = useRecoilValue(util);
  const [keyword, setKeyword] = useState<string>();
  
  const [managers, setManagers] = useState<TMember[]>([]);

  useEffect(() => {
    onChange?.(managers);
  }, [managers]);

  useEffect(() => {
    setManagers(defaultManagers);
  }, [defaultManagers]);  

  return (
      <>
          <div
              css={css`
                  display: flex;
                  gap: 4px;
                  white-space: normal;
                  flex-wrap: wrap;
              `}
          >
              {managers.map((item, index) => {
                  return (
                    index === 0 
                    ? <Button
                          key={index}
                          type="secondary"
                          title={item.nickname}
                          styles={{
                              container: css`
                                  border-radius: 100px;
                                  height: 30px;
                                  padding: 0 8px;
                                  width: max-content;
                              `,
                              text: css`
                                  font-size: 12px;
                                  font-weight: 500;
                              `,
                          }}
                      />
                    : <Button
                        key={index}
                        type="secondary"
                        title={item.nickname}
                        rightIcon={({ color }) => {
                            return <Icon name="close" width={16} color={color} />;
                        }}
                        onClick={() => {
                            setManagers((prev) => prev.filter((_, i) => i !== index));
                        }}
                        styles={{
                            container: css`
                                border-radius: 100px;
                                height: 30px;
                                padding: 0 8px;
                                width: max-content;
                            `,
                            text: css`
                                font-size: 12px;
                                font-weight: 500;
                            `,
                        }}
                    />
                  );
              })}
          </div>
          <InputField
              value={keyword}
              placeholder={translate["담당자 검색"][language]}
              onChange={(value) => setKeyword(value)}
              onChangeDebounce={async (value) => {
                  const res = await searchMembers(value);
                  if (res.success) setSearchMember(res.data ?? []);
              }}
          />
          <div
              css={css`
                  max-height: 400px;
                  overflow: auto;
                  width: 100%;
              `}
          >
              {searchMember.length === 0 && keyword && isValidEmail(keyword) && (
                  <div
                      onClick={() => {
                          if (!managers.map((item) => item.email).includes(keyword)) {
                              setManagers([...managers]);
                          } else {
                            setManagers((prev) => [...prev].filter((it) => it.email !== keyword));
                          }
                          setKeyword("");
                          setSearchMember([]);
                      }}
                      css={css`
                          width: calc(100% - 48px);
                          border: none;
                          display: flex;
                          align-items: center;
                          padding: 12px 24px;
                          border-radius: 10px;
                          gap: 8px;
                          background-color: ${[...managers].map((item) => item.email).includes(keyword) ? theme.neutral50 : ""};
                          &:hover {
                              background-color: ${theme.neutral50};
                          }
                      `}
                  >
                      {keyword}
                      {[...managers].map((item) => item.email).includes(keyword) && (
                          <span
                              css={css`
                                  color: ${theme.primary500};
                                  font-size: 14px;
                                  font-weight: 500;
                                  margin-left: auto;
                              `}
                          >
                              {translate["이미 추가된 담당자입니다."][language]}
                          </span>
                      )}
                  </div>
              )}
              {searchMember.map((item, index) => {
                  return (
                      <SearchManagerItem
                          onClick={() => {
                              if (!managers.map((item) => item.id).includes(item.id)) {
                                  setManagers([...managers, item]);
                              } else {
                                if (!(memberInfo?.id === item.id)) {
                                    setManagers((prev) => [...prev].filter((it) => it.id !== item.id));
                                }   
                              }
                              setKeyword("");
                              setSearchMember([]);
                          }}
                          key={index}
                          member={item}
                          styles={{
                              container: css`
                                  width: calc(100% - 48px);
                                  border: none;
                                  background-color: ${[...managers].map((item) => item.id).includes(item.id) ? theme.neutral50 : ""};

                                  &:hover {
                                      background-color: ${theme.neutral50};
                                  }
                              `,
                          }}
                          rightRender={() => {
                              if ([...managers].map((item) => item.id).includes(item.id)) {
                                  return (
                                      <p
                                          css={css`
                                              color: ${theme.primary500};
                                              font-size: 14px;
                                              font-weight: 500;
                                              margin-left: auto;
                                          `}
                                      >
                                          {translate["이미 추가된 담당자입니다."][language]}
                                      </p>
                                  );
                              }
                              return null;
                          }}
                      />
                  );
              })}
          </div>
      </>
  );
};